import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import SEO from '../../components/SEO';
import './Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCartItems(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await api.put(`/cart/items/${itemId}`, { quantity: newQuantity });
      fetchCart();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update quantity');
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this item from cart?')) return;

    try {
      await api.delete(`/cart/items/${itemId}`);
      fetchCart();
    } catch (err) {
      alert('Failed to remove item');
    }
  };

  const clearCart = async () => {
    if (!window.confirm('Remove all items from cart?')) return;

    try {
      await api.delete('/cart/clear');
      fetchCart();
    } catch (err) {
      alert('Failed to clear cart');
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (parseFloat(item.product.price) * item.quantity);
    }, 0);
  };

  if (loading) return <div className="loading">Loading cart...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <SEO
        title="Shopping Cart"
        description="View and manage your shopping cart"
        keywords="cart, shopping cart, checkout"
      />
      <div className="cart-page">
        <div className="container">
          <div className="cart-header">
            <h1>Shopping Cart</h1>
            {cartItems.length > 0 && (
              <button className="btn btn-secondary" onClick={clearCart}>
                Clear Cart
              </button>
            )}
          </div>

          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <div className="empty-cart-icon">🛒</div>
              <h2>Your cart is empty</h2>
              <p>Add some products to get started!</p>
              <Link to="/products" className="btn btn-primary">
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="cart-content">
              <div className="cart-items">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-image">
                      {item.product.image_url ? (
                        <img src={item.product.image_url} alt={item.product.name} />
                      ) : (
                        <div className="placeholder-image-small">No Image</div>
                      )}
                    </div>

                    <div className="cart-item-details">
                      <h3>{item.product.name}</h3>
                      {item.product.category && (
                        <span className="cart-item-category">{item.product.category}</span>
                      )}
                      {item.product.sku && (
                        <span className="cart-item-sku">SKU: {item.product.sku}</span>
                      )}
                      <p className="cart-item-price">
                        ${parseFloat(item.product.price).toFixed(2)} each
                      </p>
                    </div>

                    <div className="cart-item-quantity">
                      <label>Quantity:</label>
                      <div className="quantity-controls">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 1 && val <= item.product.stock) {
                              updateQuantity(item.id, val);
                            }
                          }}
                          min="1"
                          max={item.product.stock}
                        />
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.product.stock}
                        >
                          +
                        </button>
                      </div>
                      {item.quantity >= item.product.stock && (
                        <span className="max-stock-notice">Max available</span>
                      )}
                    </div>

                    <div className="cart-item-subtotal">
                      <span>Subtotal:</span>
                      <strong>
                        ${(parseFloat(item.product.price) * item.quantity).toFixed(2)}
                      </strong>
                    </div>

                    <button
                      className="remove-item-btn"
                      onClick={() => removeItem(item.id)}
                      title="Remove item"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="cart-summary">
                <h2>Order Summary</h2>
                <div className="summary-row">
                  <span>Subtotal ({cartItems.length} items):</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="summary-row summary-total">
                  <span>Total:</span>
                  <strong>${calculateTotal().toFixed(2)}</strong>
                </div>

                <button className="btn btn-primary btn-large btn-checkout">
                  Proceed to Checkout
                </button>

                <Link to="/products" className="continue-shopping">
                  ← Continue Shopping
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
